/**
 * When a group tries WebRTC to a peer again after a failed attempt.
 *
 * Three paths open connections on their own initiative: the event bus on
 * every peersList change, any message sent over the WebSocket relay
 * (_ensureConnecting), and the state leader reaching its members. None of them
 * backed off. Every peersList change tried every peer without an open channel,
 * a peer that dropped and rejoined the group had its backoff reset, and each
 * attempt is a new RTCPeerConnection that waits 30 s and dies. One page whose
 * WebRTC failed made every page retry it: an observatory GeoCamera created
 * about 2,500 peer connections in under 7 hours while holding 48, and its
 * CameraMap reached Chrome's cap (geocamera group, 2026-10-21).
 *
 * Now each failure pushes the next opportunistic attempt out: 15 s, 30 s,
 * 1 min, 2 min, then every 5 min. A connection that opens clears it. A peer
 * keeps its backoff across a brief absence from the group (a WebSocket that
 * dropped and reconnected); one gone FORGET_AFTER_MS or more, or never seen,
 * starts fresh. An offer from the peer is always answered, so a reloaded page
 * still connects at once from its side.
 */

export const CONNECT_BACKOFF_MS = [15_000, 30_000, 60_000, 120_000, 300_000]
export const FORGET_AFTER_MS = 5 * 60_000

export class ConnectBackoff {
    constructor({ now = () => Date.now() } = {}) {
        this.now = now
        this.entries = new Map()   // peerId → { failures, nextAt }
        this._present = new Set()  // peers in the last peersList
        this._leftAt = new Map()   // peerId → when it left the group
        this._seen = new Set()     // every peer this group has seen
    }

    /** Whether an opportunistic attempt must wait. */
    blocked(peerId) {
        const e = this.entries.get(peerId)
        return !!e && this.now() < e.nextAt
    }

    /** Ms until the next attempt may go (0: now). */
    waitMs(peerId) {
        const e = this.entries.get(peerId)
        return e ? Math.max(0, e.nextAt - this.now()) : 0
    }

    /** An attempt failed. @returns the delay before the next one */
    fail(peerId) {
        const e = this.entries.get(peerId) || { failures: 0, nextAt: 0 }
        e.failures++
        const delay = CONNECT_BACKOFF_MS[Math.min(e.failures, CONNECT_BACKOFF_MS.length) - 1]
        e.nextAt = this.now() + delay
        this.entries.set(peerId, e)
        return delay
    }

    /** A connection opened. */
    succeed(peerId) {
        this.entries.delete(peerId)
    }

    /**
     * The group's members now. @returns the peers that start fresh: never seen,
     * or back after FORGET_AFTER_MS or more away (their backoff is dropped)
     */
    seen(peerIds) {
        const now = this.now()
        const current = new Set(peerIds)
        for (const id of this._present) if (!current.has(id)) this._leftAt.set(id, now)
        const fresh = []
        for (const id of current) {
            if (this._present.has(id)) continue
            const leftAt = this._leftAt.get(id)
            if (!this._seen.has(id) || (leftAt != null && now - leftAt >= FORGET_AFTER_MS)) {
                this.entries.delete(id)
                fresh.push(id)
            }
            this._leftAt.delete(id)
            this._seen.add(id)
        }
        this._present = current
        return fresh
    }
}
